import axios from 'axios';
import { getSession } from 'next-auth/react';
import { eventBus } from './event-bus';
import { AUTH_QUERY_KEYS, queryClient } from './queryClient';

let isIntentionalLogout = false;
let isUnauthorizedRedirecting = false;

// Get API URL from environment with fallback
// Backend global prefix is "api/v1" (see packages/backend/src/main.ts)
const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000/api/v1';

// Create a plain Axios instance that works in all environments
export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(async (config) => {
  
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  // This should only run on the client-side where localStorage and getSession are available
  if (typeof window !== 'undefined') {
    try {
      // Try to get token from localStorage first for performance
      let token = localStorage.getItem('accessToken');

      // If no token in localStorage, try React Query cache
      if (!token) {
        const cachedToken = queryClient.getQueryData<string>([
          AUTH_QUERY_KEYS.token,
        ]);
        if (cachedToken) token = cachedToken;
      }

      // If still no token, get a fresh session
      if (!token) {
        const session = await getSession();
        if (session?.accessToken) {
          token = session.accessToken;
          // Cache the new token
          localStorage.setItem('accessToken', token);
          queryClient.setQueryData([AUTH_QUERY_KEYS.token], token);
        }
      }

      // Set the authorization header if we have a token
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('API request interceptor error:', error);
    }
  }
  return config;
});

// Response interceptor for handling auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      // This part should only run on the client-side
      if (typeof window !== 'undefined') {
        // Prevent redirect for specific auth recovery pages
        const currentPath = window.location.pathname;

        // Special handling for auth recovery pages where we want to allow toast but not redirect
        if (
          currentPath === '/reset-password' ||
          currentPath === '/forgot-password'
        ) {
          localStorage.removeItem('accessToken');
          queryClient.setQueryData([AUTH_QUERY_KEYS.token], null);
          const backendMessage =
            error.response?.data?.message ||
            error.response?.data?.error ||
            null;
          eventBus.emit('SHOW_TOAST', {
            message:
              backendMessage ??
              'Your session has expired. Please try your action again.',
            type: 'warning',
          });
          isUnauthorizedRedirecting = false; // Reset flag
          return Promise.reject(error);
        }

        // If on login page, just clear session and don't show toast/redirect
        if (currentPath === '/login') {
          localStorage.removeItem('accessToken');
          queryClient.setQueryData([AUTH_QUERY_KEYS.token], null);
          isUnauthorizedRedirecting = false; // Reset flag just in case
          return Promise.reject(error);
        }

        if (isIntentionalLogout) {
          localStorage.removeItem('accessToken');
          queryClient.setQueryData([AUTH_QUERY_KEYS.token], null);
          isIntentionalLogout = false;
          return Promise.reject(error);
        }

        if (isUnauthorizedRedirecting) {
          return Promise.reject(error);
        }
        isUnauthorizedRedirecting = true;
        const errorCode = error.response?.data?.code;

        if (errorCode === 'PERMISSIONS_OUT_OF_SYNC') {
          eventBus.emit('SHOW_TOAST', {
            message: 'Your permissions have changed. Please log in again.',
            type: 'warning',
          });
        } else {
          eventBus.emit('SHOW_TOAST', {
            message: 'Your session has expired. Please log in.',
            type: 'error',
          });
        }

        // Clear stored token
        localStorage.removeItem('accessToken');
        queryClient.setQueryData([AUTH_QUERY_KEYS.token], null);

        // Redirect to login after a short delay to allow toast to be seen
        setTimeout(() => {
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          isUnauthorizedRedirecting = false;
        }, 1000);
      }
    }
    return Promise.reject(error);
  },
);

export const setIntentionalLogout = (value: boolean) => {
  isIntentionalLogout = value;
};

// Server-side helper function to create an authenticated API
export const createServerApi = (token?: string) => {
  return axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
};
